import React, { useCallback, useState } from 'react';
import { OBannerQuoteProps } from './OBannerQuote.types';
import * as Styled from './OBannerQuote.styles';

// Reuse from mi-web-ui-kit
import { AEyebrow, AEyebrowVariant } from '../../atoms/AEyebrow/index';
import { MBlockquote } from '../../molecules/MBlockquote/index';
import { MBlockquoteAlignment } from '../../molecules/MBlockquote/MBlockquote.types';
import { ABodyColor, ABodySize } from '../../atoms/ABody/index';
import { MCarouselControls } from '../../molecules/MCarouselControls/index';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';

const getCarouselControlsType = (type?: MCarouselControlsType | string): MCarouselControlsType =>
  typeof type === 'string' && type.toLowerCase() === MCarouselControlsType.Numbers
    ? MCarouselControlsType.Numbers
    : MCarouselControlsType.Dots;

// Type for raw AEM JSON data
interface AEMBannerQuoteProps {
  aEyebrow?: {
    eyebrowText?: string;
  };
  mBlockquotes?: Array<{
    aBody?: {
      bodyText?: string;
    };
    aBlockquote?: {
      quoteText?: string;
    };
  }>;
  mCarouselControls?: {
    type?: MCarouselControlsType | string;
    imageOverlay?: boolean | string;
    arrows?: boolean | string;
    icon?: string;
  };
  [key: string]: unknown; // Allow for additional AEM properties
}

export function OBannerQuote(props: OBannerQuoteProps | AEMBannerQuoteProps) {
  // Handle both typed props and AEM JSON format
  const { aEyebrow, mBlockquotes: rawBlockquotes = [], mCarouselControls: rawControls } = props;

  // Process mBlockquotes - no conversion needed as structure is compatible
  const mBlockquotes = rawBlockquotes;

  // Process mCarouselControls - convert string values to appropriate types
  const mCarouselControls = rawControls
    ? {
        type: getCarouselControlsType(rawControls.type),
        imageOverlay:
          typeof rawControls.imageOverlay === 'string'
            ? rawControls.imageOverlay === 'true'
            : (rawControls.imageOverlay as boolean) || false,
        arrows: typeof rawControls.arrows === 'string' ? rawControls.arrows === 'true' : rawControls.arrows ?? true,
      }
    : undefined;

  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = mBlockquotes.length;

  const handleNext = useCallback(() => {
    setCurrentSlide(prev => (prev + 1 >= totalSlides ? 0 : prev + 1));
  }, [totalSlides]);

  const handlePrev = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 < 0 ? totalSlides - 1 : prev - 1));
  }, [totalSlides]);

  const showArrows = mCarouselControls?.arrows ?? true;

  const renderBlockquote = () => {
    if (!mBlockquotes.length) return null;
    const activeItem = mBlockquotes[currentSlide];
    const quoteText = activeItem?.aBlockquote?.quoteText || '';
    const bodyText = activeItem?.aBody?.bodyText || '';

    return (
      <MBlockquote
        alignment={MBlockquoteAlignment.Centered}
        quoteIcon
        blockquoteText={quoteText}
        bodyText={`— ${bodyText}`}
        bodyColor={ABodyColor.Default}
        bodySize={ABodySize.S}
      />
    );
  };

  return (
    <Styled.Wrapper className="grid-container">
      <div className="grid-row">
        {aEyebrow?.eyebrowText && (
          <Styled.EyebrowWrapper className="col-12">
            <AEyebrow variant={AEyebrowVariant.Primary}>{aEyebrow.eyebrowText}</AEyebrow>
          </Styled.EyebrowWrapper>
        )}

        <Styled.QuoteWrapper className="col-12">{renderBlockquote()}</Styled.QuoteWrapper>

        {!!totalSlides && (
          <Styled.CarouselWrapper className="col-12">
            <MCarouselControls
              type={mCarouselControls?.type ?? MCarouselControlsType.Dots}
              imageOverlay={mCarouselControls?.imageOverlay ?? false}
              totalSlides={totalSlides}
              currentSlide={currentSlide}
              onNext={handleNext}
              onPrev={handlePrev}
              arrows={showArrows}
            />
          </Styled.CarouselWrapper>
        )}
      </div>
    </Styled.Wrapper>
  );
}
